import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiClient from '../api/apiClient';

export default function InvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [valid, setValid] = useState(null);
  const [invite, setInvite] = useState(null);
  const [form, setForm] = useState({ name: '', password: '', confirm: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    apiClient.get(`/auth/invite/${token}`)
      .then(res => { setValid(true); setInvite(res.data); })
      .catch(() => setValid(false));
  }, [token]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) { setError('Passwords do not match.'); return; }
    if (form.password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setLoading(true); setError('');
    try {
      await apiClient.post('/auth/register', {
        token,
        name: form.name,
        password: form.password,
      });
      navigate('/login');
    } catch (e) {
      setError(e.response?.data?.error || 'Registration failed.');
    } finally { setLoading(false); }
  };

  if (valid === null) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <p style={{ color: '#64748b' }}>Validating invite…</p>
        </div>
      </div>
    );
  }

  if (valid === false) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.icon}>❌</div>
          <h2 style={styles.title}>Invalid Invite</h2>
          <p style={styles.desc}>This invite link is invalid or has already been used.</p>
          <p style={{ fontSize: '0.875rem', color: '#64748b' }}>Please contact your administrator for a new invite link.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.icon}>🎉</div>
        <h2 style={styles.title}>You're Invited!</h2>
        <p style={styles.desc}>
          You've been invited to join <strong>{invite?.accountName}</strong>
          {invite?.role && <> as a <strong style={{ textTransform: 'capitalize' }}>{invite.role}</strong></>}.
        </p>
        {invite?.email && (
          <div style={styles.emailBadge}>{invite.email}</div>
        )}

        {error && <div style={styles.error}>{error}</div>}

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.field}>
            <label style={styles.label}>Your Name</label>
            <input
              type="text"
              value={form.name}
              onChange={e => set('name', e.target.value)}
              placeholder="Full name"
              style={styles.input}
            />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Password</label>
            <input
              type="password"
              required
              value={form.password}
              onChange={e => set('password', e.target.value)}
              placeholder="Min 8 characters"
              style={styles.input}
            />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Confirm Password</label>
            <input
              type="password"
              required
              value={form.confirm}
              onChange={e => set('confirm', e.target.value)}
              placeholder="Repeat password"
              style={styles.input}
            />
          </div>
          <button type="submit" disabled={loading} style={styles.btn}>
            {loading ? 'Creating Account…' : 'Create Account & Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1rem',
  },
  card: {
    background: '#fff',
    borderRadius: '1rem',
    padding: '2.5rem',
    width: '100%',
    maxWidth: '420px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
    textAlign: 'center',
  },
  icon: { fontSize: '3rem', marginBottom: '0.75rem' },
  title: { margin: '0 0 0.5rem 0', fontSize: '1.75rem', fontWeight: '700', color: '#1e293b' },
  desc: { margin: '0 0 1rem 0', color: '#64748b', fontSize: '0.95rem' },
  emailBadge: {
    display: 'inline-block',
    background: '#ede9fe',
    color: '#6366f1',
    padding: '0.375rem 0.875rem',
    borderRadius: '999px',
    fontSize: '0.875rem',
    fontWeight: '600',
    marginBottom: '1.25rem',
  },
  error: {
    background: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#dc2626',
    padding: '0.625rem 0.875rem',
    borderRadius: '0.5rem',
    fontSize: '0.875rem',
    marginBottom: '1rem',
    textAlign: 'left',
  },
  form: { display: 'flex', flexDirection: 'column', gap: '0.875rem', textAlign: 'left' },
  field: { display: 'flex', flexDirection: 'column', gap: '0.25rem' },
  label: { fontSize: '0.8rem', fontWeight: '600', color: '#374151' },
  input: {
    padding: '0.625rem 0.875rem',
    border: '1.5px solid #e2e8f0',
    borderRadius: '0.5rem',
    fontSize: '0.95rem',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  },
  btn: {
    padding: '0.75rem',
    background: '#6366f1',
    color: '#fff',
    border: 'none',
    borderRadius: '0.5rem',
    fontSize: '0.95rem',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '0.25rem',
  },
};
